import { useValidateTableTimesheet } from '@oc/pages/importers_rsi/validations/impl/timesheet/useValidateTableTimesheet';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useTaeFlags } from '@oc/pages/settings/TimesheetSettings/useTaeFlags';
import { FieldMetadataElementValidator } from 'src/components/fieldMetadata/FieldMetadataElementValidator';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { useImportersIntl } from '../translations/useImportersIntl';
import { RsiData, RsiFieldKey, RsiTableHook, RsiTableOnErrorCallback, RsiTableValidator } from '../Types';
import { AllowedImportType, CommonFieldDefinitionData } from '../utils/useImporterData';
import { ValidationParams } from './impl/validationUtils';
import { useValidateTableAgainstReferenceIdAndFieldExistence } from './impl/refIdAndExistence/useValidateTableAgainstReferenceIdAndFieldExistence';
import { useValidateTableAgainstRetroactiveEngagementChange } from './impl/engagementRetroactiveChange/useValidateTableAgainstRetroactiveEngagementChange';
import { useValidateTableAgainstEngagementOpenChangeSet } from './impl/engagementOpenChangeSet/useValidateTableAgainstEngagementOpenChangeSet';
import { useValidateTableUsingMetadata } from './impl/metadata/useValidateTableUsingMetadata';
import { ImportEntityTypeModel } from 'src/graphql/types';

export const useTableValidation = (
  importType: AllowedImportType | undefined,
  validationParams: ValidationParams | undefined,
  fieldsForExistenceDetection: RsiFieldKey[],
  fieldsForReferenceIdValidation: RsiFieldKey[],
  urnToFieldMetadataMap: Map<String, CommonFieldDefinitionData>,
  fieldMetadataValidator: FieldMetadataElementValidator,
  openChangeSetIdsRef: MutableRefObject<Set<string>>
): RsiTableHook => {
  const intl = useImportersIntl();
  const { costCenters, timesheets } = useTaeFlags();
  const entityName = importType?.entityType ? intl[importType.entityType] : 'record';
  // We want to show the BE message for Spend Approval Limit imports as we don't create a new record.
  const rowIsCreateMessage =
    importType?.entityType === ImportEntityTypeModel.SpendApprovalLimit ? '' : intl.rowIsCreate({ entityName });
  const rowIsUpdateMessage = intl.rowIsUpdate({ entityName });

  const refIdAndExistenceValidator = useValidateTableAgainstReferenceIdAndFieldExistence(
    {
      fieldsForExistenceDetection,
      fieldsForReferenceIdValidation,
      rowIsCreateMessage,
      rowIsUpdateMessage
    },
    validationParams,
    fieldMetadataValidator
  );

  const engagementRetroActiveChangeValidator = useValidateTableAgainstRetroactiveEngagementChange();
  const engagementOpenChangeSetValidator = useValidateTableAgainstEngagementOpenChangeSet(openChangeSetIdsRef);
  const timeSheetValidator = useValidateTableTimesheet();
  const metadataValidator = useValidateTableUsingMetadata({
    fieldMetadataValidator,
    urnToFieldMetadataMap
  });

  const validators: RsiTableValidator[] = useMemo(
    () =>
      filterNotEmpty([
        refIdAndExistenceValidator,
        importType?.id === 'WORKER-RETROACTIVE-CHANGE' &&
          costCenters.retroChangeImportValidation &&
          engagementRetroActiveChangeValidator,
        importType?.id === 'TIMESHEET' && timesheets.validationForTimesheetCorrectionImport && timeSheetValidator,
        engagementOpenChangeSetValidator,
        metadataValidator
      ]),
    [
      costCenters.retroChangeImportValidation,
      timesheets.validationForTimesheetCorrectionImport,
      importType?.id,
      engagementRetroActiveChangeValidator,
      engagementOpenChangeSetValidator,
      refIdAndExistenceValidator,
      metadataValidator,
      timeSheetValidator
    ]
  );

  return useCallback(
    async (table: RsiData[], addError: RsiTableOnErrorCallback) => {
      await Promise.all(validators.map((validator) => validator(table, addError)));
      return table;
    },
    [validators]
  );
};
